/**
 * Mock data for the Operations root page.
 *
 * Temporary — remove when wired to an API (see docs/mock-data.md). Static page
 * chrome (section titles, column headers, tab/filter labels) lives in the
 * components. The promotions browser reuses the acquisition Campaigns chrome, so
 * each tab's badge count is tallied straight from this pool (see Promotions).
 *
 * A few promotions also run as Acquisition campaigns (the same offer aimed at
 * new players). Those carry a `linkKey` matching the campaign's `linkKey` in
 * `acquisition.ts`; the promotion detail uses it to show the "also in use for
 * Acquisition" crosslink and deep-link across. Promotions WITHOUT a `linkKey`
 * have no acquisition counterpart and show no crosslink — kept deliberately so
 * both cases are represented (see types.ts `Promotion.linkKey`).
 */
import type { OperationsData, Promotion, PromotionBenefit, CampaignStatus } from './types';

// The three campaign artworks rotate across the promotion thumbnails.
const IMAGES = [
  '/assets/marketing/campaign-halloween.png',
  '/assets/marketing/campaign-spooky-1.png',
  '/assets/marketing/campaign-spooky-2.png',
] as const;
const img = (i: number): string => IMAGES[i % IMAGES.length];

/** Activation benefits — the +N Conversion / Monetization / Retention uplift. */
const benefits = (conversion: number, monetization: number, retention: number): PromotionBenefit[] => [
  { id: 'conversion', label: 'Conversion', value: `+${conversion}` },
  { id: 'monetization', label: 'Monetization', value: `+${monetization}` },
  { id: 'retention', label: 'Retention', value: `+${retention}` },
];

// Suggestion expiry deadlines as `now + offset`, captured once at load time, so
// the "Expires in" timers count down live against the browser clock. The offsets
// straddle the urgency thresholds (red < 2 days, gold < 5 days, grey beyond).
const expiresIn = (d: number, h: number, m: number, s = 0): number =>
  Date.now() + (((d * 24 + h) * 60 + m) * 60 + s) * 1000;

type PromoSeed = Omit<Promotion, 'image'> & { i: number };
const build = (seed: PromoSeed): Promotion => {
  const { i, ...rest } = seed;
  return { ...rest, image: img(i) };
};

const POOL: PromoSeed[] = [
  // --- Suggestions (live "Expires in" countdown) ----------------------------
  {
    i: 0,
    id: 'sg-birthday',
    status: 'suggestion',
    category: 'promo',
    period: '20-30 Oct',
    title: 'Happy Birthday Bonus for loyal players',
    benefits: benefits(12, 6, 2),
    expiresAt: expiresIn(0, 5, 40),
  },
  {
    i: 1,
    id: 'sg-spooky',
    status: 'suggestion',
    category: 'seasonal',
    period: '22-31 Oct',
    title: 'Spooky Spins treat for dormant players',
    benefits: benefits(9, 4, 7),
    expiresAt: expiresIn(0, 0, 47, 12),
  },
  {
    i: 2,
    id: 'sg-vip-reload',
    status: 'suggestion',
    category: 'retention',
    period: 'every weekend',
    title: 'VIP Weekend Reload to lift LTV',
    benefits: benefits(4, 11, 5),
    expiresAt: expiresIn(3, 2, 14),
  },
  {
    i: 3,
    id: 'sg-winback',
    status: 'suggestion',
    category: 'acquisition',
    period: '1-7 Nov',
    title: 'Win-back 50 Free Spins for churned players',
    benefits: benefits(15, 3, 9),
    expiresAt: expiresIn(1, 6, 30),
  },
  {
    i: 4,
    id: 'sg-football',
    status: 'suggestion',
    category: 'sports',
    period: '24 Oct',
    title: 'Football Master odds boost for sports fans',
    benefits: benefits(8, 7, 3),
    expiresAt: expiresIn(5, 8, 5),
  },
  {
    i: 5,
    id: 'sg-cashback',
    status: 'suggestion',
    category: 'promo',
    period: 'monthly',
    title: 'High-roller Cashback to protect revenue',
    benefits: benefits(3, 14, 6),
    expiresAt: expiresIn(4, 4, 30),
  },
  {
    i: 6,
    id: 'sg-diwali',
    status: 'suggestion',
    category: 'seasonal',
    period: '1-5 Nov',
    title: 'Diwali Festival of Lights loyalty drop',
    benefits: benefits(10, 5, 8),
    expiresAt: expiresIn(2, 10, 0),
  },

  // --- Running (LIVE badge) -------------------------------------------------
  {
    i: 1,
    id: 'rn-halloween',
    status: 'running',
    category: 'seasonal',
    period: '22-30 Oct',
    title: 'Halloween is coming, 100 Free Spins!',
    benefits: benefits(12, 6, 2),
  },
  {
    i: 2,
    id: 'rn-copa',
    status: 'running',
    category: 'sports',
    period: '1-31 Oct',
    title: 'Copa Talento Futsal engagement boost',
    benefits: benefits(7, 4, 9),
  },
  {
    i: 0,
    id: 'rn-loyalty',
    status: 'running',
    category: 'retention',
    period: 'ongoing',
    title: 'Loyalty Ladder rewards for regulars',
    benefits: benefits(5, 8, 11),
  },
  {
    i: 1,
    id: 'rn-reactivation',
    status: 'running',
    category: 'acquisition',
    period: 'Sat-Sun',
    title: 'Weekend Reactivation push for sleepers',
    benefits: benefits(14, 5, 6),
    linkKey: 'weekend-reactivation', // also a running Acquisition campaign
  },
  {
    i: 2,
    id: 'rn-deposit',
    status: 'running',
    category: 'promo',
    period: 'daily',
    title: 'Daily Deposit Match to grow stakes',
    benefits: benefits(6, 12, 4),
  },

  // --- Paused ("Paused on …") -----------------------------------------------
  {
    i: 0,
    id: 'ps-summer',
    status: 'paused',
    category: 'seasonal',
    period: '1-30 Jun',
    title: 'Summer Splash free-spins drop',
    benefits: benefits(8, 5, 7),
    statusAt: 'Sep 12, 09:20',
    linkKey: 'summer-splash', // also a paused Acquisition campaign
  },
  {
    i: 1,
    id: 'ps-midweek',
    status: 'paused',
    category: 'sports',
    period: 'every Wed',
    title: 'Midweek Madness odds boost',
    benefits: benefits(9, 6, 3),
    statusAt: 'Sep 18, 16:05',
    linkKey: 'midweek-madness', // also a paused Acquisition campaign (the screenshot pair)
  },
  {
    i: 2,
    id: 'ps-vip',
    status: 'paused',
    category: 'retention',
    period: '10-12 Aug',
    title: 'VIP exclusive reload weekend',
    benefits: benefits(4, 13, 8),
    statusAt: 'Aug 30, 18:45',
  },
  {
    i: 0,
    id: 'ps-school',
    status: 'paused',
    category: 'acquisition',
    period: '1-7 Sep',
    title: 'Back to School deposit bonus',
    benefits: benefits(11, 4, 5),
    statusAt: 'Sep 02, 11:10',
    linkKey: 'back-to-school', // also a paused Acquisition campaign
  },
  {
    i: 1,
    id: 'ps-mystery',
    status: 'paused',
    category: 'promo',
    period: 'every Mon',
    title: 'Mystery Box Mondays giveaway',
    benefits: benefits(7, 9, 6),
    statusAt: 'Sep 23, 12:00',
  },

  // --- Ended ("Ended on …") -------------------------------------------------
  {
    i: 2,
    id: 'en-euro',
    status: 'ended',
    category: 'sports',
    period: '14 Jun-14 Jul',
    title: 'Euro Special acca insurance',
    benefits: benefits(13, 7, 4),
    statusAt: 'Jul 14, 23:00',
    linkKey: 'euro-special-acca', // also ran as an Acquisition campaign
  },
  {
    i: 0,
    id: 'en-spring',
    status: 'ended',
    category: 'seasonal',
    period: '1-31 May',
    title: 'Spring Jackpot Race $50k pool',
    benefits: benefits(9, 11, 5),
    statusAt: 'May 31, 21:30',
    linkKey: 'spring-jackpot-race', // also ran as an Acquisition campaign
  },
  {
    i: 1,
    id: 'en-newyear',
    status: 'ended',
    category: 'seasonal',
    period: '31 Dec-1 Jan',
    title: 'New Year Mega Drop celebration',
    benefits: benefits(15, 8, 9),
    statusAt: 'Jan 02, 02:00',
    linkKey: 'new-year-mega-drop', // also ran as an Acquisition campaign
  },
  {
    i: 2,
    id: 'en-valentine',
    status: 'ended',
    category: 'promo',
    period: '10-15 Feb',
    title: "Valentine's Match Bonus for couples",
    benefits: benefits(6, 5, 12),
    statusAt: 'Feb 15, 12:00',
    linkKey: 'valentines-match-bonus', // also ran as an Acquisition campaign
  },
  {
    i: 0,
    id: 'en-refer',
    status: 'ended',
    category: 'acquisition',
    period: '1-30 Apr',
    title: 'Refer & Earn Spring drive',
    benefits: benefits(14, 3, 7),
    statusAt: 'Apr 30, 18:00',
    linkKey: 'refer-earn-spring', // also ran as an Acquisition campaign
  },
  {
    i: 1,
    id: 'en-wimbledon',
    status: 'ended',
    category: 'sports',
    period: '1-14 Jul',
    title: 'Wimbledon Match Bonus fortnight',
    benefits: benefits(8, 6, 4),
    statusAt: 'Jul 14, 20:00',
    linkKey: 'wimbledon-match-bonus', // also ran as an Acquisition campaign
  },
  {
    i: 2,
    id: 'en-carnival',
    status: 'ended',
    category: 'promo',
    period: '10-20 Feb',
    title: 'Carnival Cashback fiesta',
    benefits: benefits(5, 10, 6),
    statusAt: 'Feb 20, 21:00',
  },
];

const items: Promotion[] = POOL.map(build);

const tally = (status: CampaignStatus): number => items.filter((p) => p.status === status).length;

export const operationsData: OperationsData = {
  insights: {
    summary: {
      title: 'Your player base is doing great!',
      score: 96,
      status: 'Optimal',
      tone: 'optimal',
      progress: 96,
    },
    metrics: [
      { id: 'conversion', title: 'Conversion', score: 84, tone: 'sustainable', progress: 84 },
      { id: 'monetization', title: 'Monetization', score: 48, tone: 'poor', progress: 48 },
      { id: 'retention', title: 'Retention', score: 95, tone: 'optimal', progress: 95 },
    ],
  },

  // The badge counts are derived from the pool by the Promotions browser; `count`
  // here mirrors those tallies so the declared and rendered numbers agree.
  promotions: {
    tabs: [
      { id: 'suggestions', count: tally('suggestion'), badgeColor: 'magenta' },
      { id: 'running', count: tally('running'), badgeColor: 'green' },
      { id: 'paused', count: tally('paused'), badgeColor: 'muted' },
      { id: 'ended', count: tally('ended'), badgeColor: 'muted' },
    ],
    filters: [
      { value: 'seasonal' },
      { value: 'sports' },
      { value: 'promo' },
      { value: 'acquisition' },
      { value: 'retention' },
    ],
    items,
    hasMore: true,
  },

  // Order is the logical / mobile reading order (= the Figma child order). The
  // desktop frame floats Support Cases into the top-right, which AutomatedStats
  // reproduces with CSS `order` so a single array drives both breakpoints.
  automated: {
    stats: [
      { id: 'campaigns', icon: 'megaphone', value: '323', label: 'Campaigns' },
      { id: 'targeted', icon: 'people', value: '125,023', label: 'Targeted Players' },
      { id: 'notifications', icon: 'bell', value: '15,089', label: 'Sent Notifications' },
      { id: 'activated', icon: 'member', value: '45,023', label: 'Activated Players' },
      { id: 'support', icon: 'handshake', value: '5,027', label: 'Support Cases' },
    ],
  },
};

export default operationsData;
